/**
 * The platform session — who is signed in, which clubs they belong to, which
 * club is ACTIVE for this browser, and the three hats a single account can
 * wear (club member/committee, portal owner, Super Admin).
 *
 * One account per email (SRS §6.1); roles live per club and are resolved by
 * the server per request. This context only remembers the selection: which
 * club the person is looking at. Permissions come back with the club payload
 * and every API call re-checks them server-side.
 *
 * Sits ABOVE ClubProvider: ClubProvider loads the selected club's data when
 * `activeClubId` settles, and reloads when it changes.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { api, getToken, setClubId, setSessionLostHandler, setToken } from '../lib/api';

const AuthContext = createContext(null);
const ACTIVE_CLUB_KEY = 'platform_active_club';

function readStoredClub() {
  try {
    return localStorage.getItem(ACTIVE_CLUB_KEY);
  } catch {
    return null;
  }
}

function storeClub(clubId) {
  try {
    if (clubId) localStorage.setItem(ACTIVE_CLUB_KEY, clubId);
    else localStorage.removeItem(ACTIVE_CLUB_KEY);
  } catch {
    /* private windows */
  }
}

/**
 * Where a fresh session should land (FR-AU-03/04): one club and nothing else
 * → straight in; anything more — several clubs, an owned portal, the platform
 * console — → the selector, which shows every hat.
 */
export function landingFor(session, activeClubId) {
  const memberships = session.memberships ?? [];
  const owned = session.ownedPortals ?? [];
  const usable = memberships.filter((m) => m.accessState === 'active' || m.accessState === 'grace');

  if (activeClubId && usable.some((m) => m.clubId === activeClubId)) {
    return { kind: 'club', clubId: activeClubId };
  }
  if (!session.user.isSuperAdmin && owned.length === 0 && usable.length === 1) {
    return { kind: 'club', clubId: usable[0].clubId };
  }
  return { kind: 'select' };
}

export function AuthProvider({ children }) {
  const [booting, setBooting] = useState(true);
  const [session, setSession] = useState(null); // { user, memberships, ownedPortals }
  const [activeClubId, setActiveClubIdState] = useState(null);

  const applySelection = useCallback((clubId) => {
    setClubId(clubId);
    storeClub(clubId);
    setActiveClubIdState(clubId);
  }, []);

  const clearSession = useCallback(() => {
    setToken(null);
    storeClub(null);
    setClubId(null);
    setSession(null);
    setActiveClubIdState(null);
  }, []);

  useEffect(() => {
    setSessionLostHandler(clearSession);
  }, [clearSession]);

  /* Cold start: a stored token either restores the whole session or dies. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!getToken()) {
        setBooting(false);
        return;
      }
      try {
        const s = await api.session();
        if (cancelled) return;
        setSession(s);
        const stored = readStoredClub();
        const landing = landingFor(s, stored);
        if (landing.kind === 'club') applySelection(landing.clubId);
      } catch {
        if (!cancelled) clearSession();
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applySelection, clearSession]);

  const signIn = useCallback(
    async (identifier, password) => {
      const payload = await api.signIn(identifier, password);
      setSession(payload);
      const landing = landingFor(payload, readStoredClub());
      if (landing.kind === 'club') applySelection(landing.clubId);
      else applySelection(null);
      return { session: payload, landing };
    },
    [applySelection],
  );

  /** After accepting an invitation — the API signed them in already. */
  const adoptSession = useCallback(
    (payload) => {
      setSession(payload);
      const landing = landingFor(payload, null);
      if (landing.kind === 'club') applySelection(landing.clubId);
      return landing;
    },
    [applySelection],
  );

  const selectClub = useCallback(
    (clubId) => {
      applySelection(clubId);
    },
    [applySelection],
  );

  const signOut = useCallback(() => {
    clearSession();
  }, [clearSession]);

  /** Re-pull relationships (e.g. after creating a club or a portal action). */
  const refreshSession = useCallback(async () => {
    const s = await api.session();
    setSession(s);
    return s;
  }, []);

  const value = useMemo(() => {
    const memberships = session?.memberships ?? [];
    return {
      booting,
      signedIn: session != null,
      user: session?.user ?? null,
      memberships,
      ownedPortals: session?.ownedPortals ?? [],
      isSuperAdmin: session?.user?.isSuperAdmin ?? false,
      /* Read-only demo account. The API enforces it on every write; this is
         only so the shells can SAY so before someone tries. */
      isDemo: session?.user?.isDemo ?? false,
      activeClubId,
      activeMembership: memberships.find((m) => m.clubId === activeClubId) ?? null,
      signIn,
      signOut,
      selectClub,
      adoptSession,
      refreshSession,
    };
  }, [booting, session, activeClubId, signIn, signOut, selectClub, adoptSession, refreshSession]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
